import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {StorageMap} from '@ngx-pwa/local-storage';
import {ToolsService} from '../tools/tools.service';

/** Chemins des pages WeFit LMS (à concaténer après wefitEndpoint) */
export const WEFIT_PAGES = {
  home:         '',
  visio:        'common_tp_details.cfm',
  virtualClass: 'learner_virtual.cfm',
  profile:      'common_learner_account.cfm',
  examPrep:     'index.cfm',
  weAdvance:    'el_dashboard.cfm',
  // Pages LMS2 (Visio INDIV / INTRA / INTER)
  visioIndiv:   'visio_index.cfm',
  visioIntra:   'group_index.cfm',
  visioInter:   'vc_index.cfm'
} as const;

/** Correspondance code langTgt GS → ID langue WeFit */
const GS_TO_WEFIT_LANG: Record<string, number> = {
  fr: 1, en: 2, de: 3, es: 4, it: 5, ar: 6,
  zh: 8, nl: 9, pl: 11, pt: 12, ru: 13,
  ja: 14, da: 15, le: 19, sv: 21, tr: 22,
  hu: 23, ro: 22, hr: 25, th: 29, ko: 30
};

/** IDs WeFit des langues supportées par feature */
const WEFIT_FEATURE_LANGS: Record<string, number[]> = {
  visio:   [1, 2, 3, 4, 5, 6, 8, 13, 14],
  vc:      [2],
  vintra:  [],
  advance: [1, 2, 3, 4, 5],
  game:    [1, 2, 3, 4, 5, 6, 8, 9, 11, 12, 13, 14, 15, 19, 21, 22, 23, 25, 29, 30],
  exam:    [2],
};

/**
 * Retourne true si la feature WeFit est disponible pour le code langTgt GS donné.
 * Retourne false si le code est inconnu ou la feature non supportée.
 */
export function isWefitFeatureAvailable(feature: string, langTgtCode: string | undefined): boolean {
  if (!langTgtCode) { return false; }
  const wefitId = GS_TO_WEFIT_LANG[langTgtCode];
  if (!wefitId) { return false; }
  return WEFIT_FEATURE_LANGS[feature]?.includes(wefitId) ?? false;
}

@Injectable({
  providedIn: 'root'
})
export class WefitSsoService {

  private readonly STORAGE_KEY = 'wefit-token';
  private readonly STORAGE_KEY_URL = 'wefit-sso-url';
  private readonly STORAGE_KEY_PROVIDER = 'wefit-provider-id';

  constructor(
    private http: HttpClient,
    private storage: StorageMap,
    private toolsSrv: ToolsService
  ) {
  }

  /**
   * Returns the stored Wefit token, or null if not present.
   */
  async getStoredToken(): Promise<string | null> {
    const token = await this.storage.get(this.STORAGE_KEY).toPromise();
    return (token as string) ?? null;
  }

  /**
   * Returns the stored Wefit SSO URL, or null if not present.
   */
  async getStoredSsoUrl(): Promise<string | null> {
    const url = await this.storage.get(this.STORAGE_KEY_URL).toPromise();
    return (url as string) ?? null;
  }

  /**
   * Fetches the Wefit SSO token (and sso_url) from the GS API and stores them locally.
   * Silently fails if Wefit is unavailable or returns no token.
   */
  async fetchAndStoreToken(): Promise<void> {
    try {
      const result: any = await this.http
        .get(`${this.toolsSrv.getApiEndpoint()}sso/wefit-token`)
        .toPromise();

      if (result?.token) {
        await this.storage.set(this.STORAGE_KEY, result.token).toPromise();
      }
      if (result?.sso_url) {
        await this.storage.set(this.STORAGE_KEY_URL, result.sso_url).toPromise();
      }
      if (result?.provider_id) {
        await this.storage.set(this.STORAGE_KEY_PROVIDER, result.provider_id).toPromise();
      }
    } catch (e) {
      console.warn('GS : Wefit SSO token unavailable', e?.message);
    }
  }

  /**
   * Returns the stored token, fetching it from the API if not yet present.
   */
  async getToken(): Promise<string | null> {
    const stored = await this.getStoredToken();
    if (stored) {
      return stored;
    }
    await this.fetchAndStoreToken();
    return this.getStoredToken();
  }

  /**
   * Returns the SSO URL returned by the API (marque blanche), fetching it if not yet present.
   */
  async getSsoUrl(): Promise<string | null> {
    const stored = await this.getStoredSsoUrl();
    if (stored) {
      return stored;
    }
    await this.fetchAndStoreToken();
    return this.getStoredSsoUrl();
  }

  /**
   * Returns the stored provider_id, or null if not present.
   */
  async getProviderId(): Promise<number | null> {
    const id = await this.storage.get(this.STORAGE_KEY_PROVIDER).toPromise();
    return (id as number) ?? null;
  }

  /**
   * Retourne true si le sous-domaine de la SSO URL est 'lms2'.
   */
  async isLms2(): Promise<boolean> {
    const url = await this.getSsoUrl();
    if (!url) { return false; }
    try {
      const hostname = new URL(url).hostname; // ex: "lms2.wefit.fr"
      return hostname.split('.')[0] === 'lms2';
    } catch {
      return false;
    }
  }

  /**
   * Removes the stored Wefit token, SSO URL and provider_id (call on logout).
   */
  async clearToken(): Promise<void> {
    await this.storage.delete(this.STORAGE_KEY).toPromise();
    await this.storage.delete(this.STORAGE_KEY_URL).toPromise();
    await this.storage.delete(this.STORAGE_KEY_PROVIDER).toPromise();
  }
}
